import { useState } from 'react';
import { Warning } from '@phosphor-icons/react';
import { Modal, Field, SubmitButton } from './ui';
import { useDeleteAgency } from '../queries/platform';
import { apiError } from '../lib/api';
import type { Agency } from '../lib/types';

/**
 * Deleting an agency: the one irreversible action in this console.
 *
 * The confirmation is a **typed agency name**, not an "Are you sure?". A
 * yes/no dialog on a destructive action is muscle memory within a week, and the
 * thing being destroyed is a real business's entire workspace. Typing the name
 * is the only cheap way to make the operator read *which* agency they are on —
 * the mistake this exists to prevent is deleting the wrong one, not deleting
 * one at all.
 *
 * The same two checks run server-side (`platformController.deleteAgency`), so
 * a stray API call cannot skip them. This dialog is the courtesy, not the gate.
 */
export default function DeleteAgencyModal({
  agency,
  onClose,
  onDeleted,
}: {
  agency: Agency;
  onClose: () => void;
  onDeleted: (summary: string) => void;
}) {
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const remove = useDeleteAgency();

  const nameMatches = confirm.trim().toLowerCase() === agency.name.trim().toLowerCase();
  const ready = nameMatches && reason.trim().length > 0;

  const submit = async () => {
    if (!ready) return;
    setError('');
    try {
      const result = await remove.mutateAsync({
        id: agency._id,
        confirm: confirm.trim(),
        reason: reason.trim(),
      });
      const total = Object.values(result.removed ?? {}).reduce((sum, n) => sum + n, 0);
      onDeleted(`${agency.name} deleted, with ${total} record${total === 1 ? '' : 's'} removed.`);
    } catch (err) {
      setError(apiError(err, 'Could not delete this agency'));
    }
  };

  return (
    <Modal
      title={`Delete ${agency.name}?`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </button>
          <SubmitButton
            className="btn btn-danger"
            pending={remove.isPending}
            pendingLabel="Deleting…"
            onClick={submit}
            disabled={!ready}
          >
            Delete permanently
          </SubmitButton>
        </>
      }
    >
      <div className="delete-warning">
        <Warning size={18} weight="fill" />
        <div>
          <strong>This cannot be undone.</strong>
          <p className="t-sm" style={{ margin: '5px 0 0', lineHeight: 1.55 }}>
            Every property, client, agent, visit, deal, document and uploaded file
            belonging to {agency.name} is removed, along with their sign-in. Their
            registration goes too, because it holds a real person's contact details.
          </p>
        </div>
      </div>

      <div className="stack" style={{ gap: 14, marginTop: 16 }}>
        <Field label={`Type "${agency.name}" to confirm`}>
          <input
            className="input"
            name="confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={agency.name}
            autoComplete="off"
            aria-label="Type the agency name to confirm"
          />
        </Field>

        <Field label="Reason">
          <input
            className="input"
            name="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Agency closed and asked us to remove their data"
            aria-label="Reason for deleting"
          />
          {/* Kept in AgencyDeletionLog, which outlives the agency — it is the
              only answer to "why is this customer gone?" months later. */}
          <p className="t-xs muted" style={{ margin: '6px 0 0' }}>
            Recorded against the deletion, permanently.
          </p>
        </Field>

        {error && (
          <p className="field-error" role="alert" style={{ margin: 0 }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
